/* ==========================================================================
   Join the Movement — homepage modal

   Native <dialog>: focus trap, Esc and background inerting come free from
   the platform. This file owns three things the platform does not give us —
   when the modal opens, whether it should open at all, and submitting the
   form without navigating away from the homepage.
   ========================================================================== */

(function () {
  "use strict";

  const modal = document.querySelector("[data-movement-modal]");
  if (!modal || typeof modal.showModal !== "function") return;

  const KEY = "gg:movement";
  /* Set just before the form navigates away, read on the way back so the
     confirmation can be shown once. Session-scoped: it should not survive
     into a later visit. */
  const DONE_KEY = "gg:movement:justJoined";
  /* Set when the launcher itself is dismissed — the visitor's "no". */
  const TAB_KEY = "gg:movement:tabClosed";
  const days = Number(modal.dataset.rememberDays || 30);
  let delay = Number(modal.dataset.delay || 700);

  /* Build-time escape hatches from the frequency cap below. See the
     "Preview override" note further down for what each one does. */
  const params = new URLSearchParams(window.location.search);
  const flag = params.get("movement");
  const preview =
    flag === "preview" ||
    flag === "1" ||
    Boolean(window.Shopify && window.Shopify.designMode);

  if (params.has("movement_delay")) {
    const override = Number(params.get("movement_delay"));
    if (Number.isFinite(override) && override >= 0) delay = override;
  }

  const form = modal.querySelector("[data-movement-form]");
  const done = modal.querySelector("[data-movement-done]");
  const submit = modal.querySelector("[data-movement-submit]");
  const submitLabel = modal.querySelector("[data-movement-submit-label]");
  const failure = modal.querySelector("[data-movement-failure]");
  const tab = document.querySelector("[data-movement-tab]");
  const tabOpen = tab && tab.querySelector("[data-movement-tab-open]");
  const tabClose = tab && tab.querySelector("[data-movement-tab-close]");

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* Set when this browser has just submitted and Shopify has redirected back:
     the modal reopens on its confirmation once the helpers below exist. */
  let showConfirmation = false;

  /* --- Frequency ---------------------------------------------------------
     Suppressed if they already joined, or dismissed inside the window.
     Joining is remembered indefinitely; a dismissal expires so the list
     still gets a second ask from a repeat visitor months later. */

  /* Whether this browser has already joined — distinct from suppressed(),
     which is also true during a dismissal window. The launcher keys off this
     one so a dismissal parks it rather than removing it. */
  const joinedAlready = () => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? Boolean(JSON.parse(raw).joined) : false;
    } catch (e) {
      return false;
    }
  };

  const suppressed = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return false;
      const state = JSON.parse(raw);
      if (state.joined) return true;
      return Date.now() < Number(state.until || 0);
    } catch (e) {
      return false;
    }
  };

  const remember = (joined) => {
    /* In preview the visit is not recorded, so a reload shows the modal
       again and the next tweak is one refresh away. */
    if (preview) return;
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({
          joined: Boolean(joined),
          until: Date.now() + days * 864e5,
        })
      );
    } catch (e) {}
  };

  /* --- Preview override --------------------------------------------------
     The frequency cap above means the modal is a one-shot per browser,
     which makes it near-impossible to iterate on. Two escape hatches, both
     for building only — neither is reachable by an ordinary visitor:

       ?movement=preview   open once, ignoring the cap, and do not record
                           this visit (so a reload shows it again)
       ?movement=reset     wipe the stored state and carry on normally

     Add &movement_delay=0 to skip the wait. In the theme editor
     (Shopify.designMode) the cap is ignored automatically, so the section
     can be styled live. */

  if (flag === "reset") {
    try {
      localStorage.removeItem(KEY);
      localStorage.removeItem(TAB_KEY);
    } catch (e) {}
  }

  /* Shopify redirects back here after a customer post. Record the signup, and
     if this browser is the one that just submitted, reopen the modal on its
     confirmation rather than leaving the visitor with no acknowledgement. */
  if (params.get("customer_posted") === "true") {
    remember(true);

    let justJoined = false;
    try {
      justJoined = sessionStorage.getItem(DONE_KEY) === "1";
      sessionStorage.removeItem(DONE_KEY);
    } catch (e) {}

    if (justJoined && done) {
      /* Swap to the confirmation now, but leave opening it until lockScroll
         and friends are defined further down - reaching for them here is a
         temporal dead zone error that silently stops the whole script. */
      if (form) form.hidden = true;
      done.hidden = false;
      showConfirmation = true;
    } else {
      return;
    }
  }

  /* The frequency cap does not apply to the confirmation: remember(true) has
     just run, so suppressed() is true and this would return before the
     just-joined visitor ever sees their acknowledgement. */
  /* --- Minimised launcher ------------------------------------------------
     Dismissing the modal parks the offer in the corner rather than removing
     it: someone who closed the popup reflexively can still join without
     hunting for the form. The launcher's own X is the real dismissal, and it
     is remembered for the session so it does not spring back on every page.

     Never shown to a visitor who has already joined, and not in the theme
     editor, where a fixed pill would sit over the section being styled. */

  const dismissedTab = () => {
    try {
      return localStorage.getItem(TAB_KEY) === "1";
    } catch (e) {
      return false;
    }
  };

  const showTab = () => {
    if (!tab || joinedAlready() || dismissedTab()) return;
    if (window.Shopify && window.Shopify.designMode) return;
    tab.hidden = false;
  };

  const hideTab = () => {
    if (tab) tab.hidden = true;
  };

  /* Bound once, however the launcher first appears — on dismissal, or
     immediately for a visitor returning inside the window. */
  let tabBound = false;

  const bindTab = () => {
    if (tabBound || !tab) return;
    tabBound = true;

    if (tabOpen) {
      tabOpen.addEventListener("click", () => {
        /* Reopening bypasses the frequency cap on purpose: the visitor asked
           for it. The launcher hides so the two are never both on screen. */
        hideTab();
        if (modal.open) return;
        lockScroll();
        modal.showModal();
        const landing = modal.querySelector("[data-movement-focus]");
        if (landing) landing.focus({ preventScroll: true });
      });
    }

    if (tabClose) {
      tabClose.addEventListener("click", () => {
        hideTab();
        /* The real dismissal, and the last the visitor sees of the offer:
           closing the launcher retires it for the whole remembered window,
           not just this session. Dismissing the modal is "not now"; this is
           "no" — and a second X that quietly came back tomorrow would make
           the first one feel ignored. */
        try {
          localStorage.setItem(TAB_KEY, "1");
        } catch (e) {}
      });
    }
  };

  if (!showConfirmation) {
    if (!preview && suppressed()) {
      /* Inside the dismissal window the modal stays shut, but the offer is
         still on the table: a returning visitor who closed it last time gets
         the launcher rather than nothing. Someone who has joined, or who
         dismissed the launcher itself this session, gets neither. */
      if (!joinedAlready() && !dismissedTab()) {
        bindTab();
        showTab();
      }
      return;
    }
  }

  /* --- Scroll lock -------------------------------------------------------
     <dialog> inerts the page but does not stop it scrolling behind the
     modal. Pin the body at its current offset and restore on close, so the
     homepage is exactly where they left it. */

  let scrollY = 0;

  const lockScroll = () => {
    scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = -scrollY + "px";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  };

  const unlockScroll = () => {
    const body = document.body;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    window.scrollTo(0, scrollY);
  };

  /* --- Open / close ------------------------------------------------------ */

  const open = () => {
    if (modal.open) return;
    /* Record the showing here, not on close. Recording only when the modal is
       dismissed meant anyone who left the page with it still open — closed
       the tab, hit back, navigated away — was never marked as having seen it,
       and got the popup again on their next visit. Writing on open makes it
       genuinely once per browser however the visit ends. */
    remember(false);
    lockScroll();
    modal.showModal();
    /* Land focus on the brand panel rather than the close button, so the
       dialog is announced by its title and Tab moves forward into the form.
       showModal() sets focus itself, so this has to follow it. */
    const landing = modal.querySelector("[data-movement-focus]");
    if (landing) landing.focus({ preventScroll: true });
  };

  /* The signup that just completed. Everything open() needs exists by now. */
  if (showConfirmation) {
    window.setTimeout(() => {
      if (modal.open) return;
      lockScroll();
      modal.showModal();
      const cta = done.querySelector("button");
      if (cta) cta.focus();
    }, 400);
  }

  /* Hold the dialog open for the length of the exit animation, then close
     for real. Reduced motion skips straight to the close. */
  let closing = false;

  const close = (joined) => {
    if (closing || !modal.open) return;
    remember(joined);

    /* A dismissal parks the offer in the corner; joining retires it. */
    if (joined) {
      hideTab();
    } else {
      bindTab();
      showTab();
    }

    if (reduced.matches) {
      modal.close();
      return;
    }

    closing = true;
    modal.classList.add("is-closing");

    const finish = () => {
      modal.classList.remove("is-closing");
      closing = false;
      modal.close();
    };

    modal.addEventListener("animationend", finish, { once: true });
    /* If the animation never fires (element hidden, animation stripped by a
       user stylesheet), close anyway rather than trapping the visitor. */
    window.setTimeout(() => {
      if (closing) finish();
    }, 600);
  };

  modal.querySelectorAll("[data-movement-close]").forEach((btn) => {
    /* The confirmation's button closes a modal they did join. */
    const joined = btn.closest("[data-movement-done]") !== null;
    btn.addEventListener("click", () => close(joined));
  });

  /* Clicking the backdrop closes. <dialog> reports backdrop clicks as clicks
     on the dialog itself, so compare against the dialog box. Not offered
     once the form is submitting, and not over the confirmation, where an
     accidental click would wipe the only copy of the message. */
  modal.addEventListener("click", (e) => {
    if (e.target !== modal) return;
    if (submit && submit.getAttribute("aria-busy") === "true") return;
    close(done && !done.hidden);
  });

  /* Esc fires `cancel` before `close`. Take it over so the exit animation
     runs, and so a dismissal is recorded the same way as the X. */
  modal.addEventListener("cancel", (e) => {
    e.preventDefault();
    close(done && !done.hidden);
  });

  modal.addEventListener("close", unlockScroll);

  /* --- Submission --------------------------------------------------------
     The form submits normally, exactly like the Join page: the browser POSTs
     to Shopify, Shopify redirects back with ?customer_posted=true, and the
     handler near the top of this file reopens the modal on its confirmation.

     An earlier version posted over fetch and inspected the response to decide
     whether Shopify had accepted the signup. It guessed wrong often enough to
     show "something went wrong" on signups that had in fact worked, so that
     whole path is gone rather than patched again.

     To route this to Klaviyo/Attentive instead, intercept the submit event
     here - but then the success and failure states become yours to prove. */

  const setBusy = (busy) => {
    if (!submit) return;
    submit.setAttribute("aria-busy", String(busy));
    submit.disabled = busy;
    if (submitLabel) {
      if (busy) {
        submitLabel.dataset.idle = submitLabel.textContent;
        submitLabel.textContent = submit.dataset.pendingLabel || "Joining…";
      } else if (submitLabel.dataset.idle) {
        submitLabel.textContent = submitLabel.dataset.idle;
      }
    }
  };


  if (form) {
    form.addEventListener("submit", (e) => {
      /* goopie.js validates first and calls preventDefault when a field is
         bad. Nothing to submit on that pass. */
      if (e.defaultPrevented) return;

      /* Deliberately NOT intercepted. This used to post over fetch and decide
         success by inspecting the response, which reported accepted signups as
         failures - the Join page, which submits normally, never had that
         problem because Shopify handles the whole round trip itself.

         So the modal now submits the same way: the browser POSTs, Shopify
         redirects back, and the customer_posted check at the top of this file
         reopens the modal on its confirmation. Nothing here has to guess
         whether Shopify said yes. */
      if (failure) failure.textContent = "";
      setBusy(true);

      /* Remember the signup before the page unloads, so the confirmation can
         be shown on the way back in and the modal never re-asks. */
      remember(true);
      try {
        sessionStorage.setItem(DONE_KEY, "1");
      } catch (err) {}
    });
  }

  /* --- Trigger -----------------------------------------------------------
     Wait for the preloader curtain so the two overlays never overlap. The
     preloader adds .is-revealed to <html> when it is done; on a returning
     visitor the blocking script in theme.liquid has already set it. */

  /* Exactly one open per page load, whatever gets here first. The preloader
     path and the safety net below both race to trigger, and the net used to
     fire unconditionally at 6s: by then the visitor had usually opened AND
     closed the modal, and `open()`'s own `modal.open` guard no longer held —
     so it reopened on someone who had just dismissed it. Latching here means
     neither path can fire twice, and a dismissal stays dismissed. */
  let triggered = false;

  const armed = () => {
    if (triggered) return;
    triggered = true;
    window.setTimeout(open, delay);
  };

  if (document.documentElement.classList.contains("is-revealed")) {
    armed();
  } else {
    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains("is-revealed")) {
        observer.disconnect();
        armed();
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    /* Safety net: if the preloader never reports back, still open — but only
       if the observer path never ran. */
    window.setTimeout(() => {
      observer.disconnect();
      armed();
    }, 6000);
  }

})();
